/**
 * AssistantDock — the two-column layout that docks the assistant beside a run's screen (mission 8
 * WP08 T039; FR-012, NFR-007, C-012; research §1.19, §1.20; style guide §7.12; squad U14, S23).
 *
 * Renders `AssistantPanelProvider` for the run, so the panel's store (rule context, prefill, draft)
 * lives while the run's Results or Rejected tree is mounted (OR-9). Open (`?assistant=open`): a grid of
 * the content and a `--assistant-width` column holding `panel` in a sticky `aside` under the top bar,
 * with a 1 px `navy-200` left border and no shadow (§7.12 wins over the token table; C-014), its
 * entrance a `--duration-slow` transition only when motion is allowed. Closed: the children alone, in
 * the same wrapper, so opening or closing never remounts the screen.
 *
 * **Every close** (Close, Esc, or a history move such as Back that drops `?assistant=open`; the store's
 * `closePanel` is itself a `navigate(-1)` when it pushed the open) is finished here, when `open` turns
 * from true to false: after the panel's unmount, so its query observers are gone, the run's
 * conversation is removed (P13, NFR-010: a reopen reads page 1 only), and focus goes to the rail's
 * `[data-assistant-opener]`, read now that the rail is back, else the page `h1` (OR-10, FR-025).
 * StrictMode's simulated remount never changes `open`, so it removes nothing.
 */
import { useEffect, useRef, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { cn } from '@/components/ui/cn'
import { AssistantPanelProvider, useAssistantPanel } from '@/lib/assistant-panel'
import { copyText } from '@/lib/provisional-copy'
import { removeConversation } from '@/lib/queries'

export interface AssistantDockProps {
  runId: string
  /** The panel, rendered only while open (WP09 passes `<AssistantPanel …/>`). */
  panel: ReactNode
  children: ReactNode
}

/** After a close: the opener if the screen shows one, else the page heading (made focusable). */
function returnFocus() {
  const target =
    document.querySelector<HTMLElement>('[data-assistant-opener]') ?? document.querySelector<HTMLElement>('h1')
  if (target === null) return
  if (target.tagName === 'H1' && !target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1')
  target.focus()
}

function Layout({ runId, panel, children }: AssistantDockProps) {
  const { open } = useAssistantPanel()
  const qc = useQueryClient()
  const wasOpen = useRef(open)
  useEffect(() => {
    if (wasOpen.current && !open) {
      removeConversation(qc, runId)
      returnFocus()
    }
    wasOpen.current = open
  }, [open, qc, runId])
  return (
    <div
      data-assistant-dock={open ? 'open' : 'closed'}
      className={cn(open && 'grid grid-cols-[minmax(0,1fr)_var(--assistant-width)] items-start gap-6')}
    >
      <div className="min-w-0">{children}</div>
      {open ? (
        <aside
          aria-label={copyText('assistantTitle')}
          className="sticky top-[calc(56px+var(--content-padding))] flex h-[calc(100vh-56px-var(--content-padding)*2)] flex-col border-l border-navy-200 bg-white motion-safe:transition-opacity motion-safe:duration-[var(--duration-slow)] motion-safe:ease-[var(--ease-out)]"
        >
          {panel}
        </aside>
      ) : null}
    </div>
  )
}

export function AssistantDock({ runId, panel, children }: AssistantDockProps) {
  return (
    <AssistantPanelProvider runId={runId}>
      <Layout runId={runId} panel={panel}>
        {children}
      </Layout>
    </AssistantPanelProvider>
  )
}
